"use client"

import { CalendarIcon, CandlesIcon, NewsIcon, TrendUpIcon } from "@src/icons"
import { type ReactNode, useId } from "react"
import AssetComboIcon from "../DefuseSDK/components/Asset/AssetComboIcon"

const earningsLogos = [
  "/static/icons/market/coin.svg",
  "/static/icons/market/sndk.svg",
  "/static/icons/market/aapl.svg",
] as const

const SPARKLINE_VALUES = [88, 82, 85, 68, 43, 34, 34, 23, 47, 37, 36, 22]
const SPARKLINE_WIDTH = 200
const SPARKLINE_HEIGHT = 52
const SPARKLINE_END_X = SPARKLINE_WIDTH * 0.5

function LiquidationSparkline() {
  const gradientId = `ai-perps-spark-${useId().replaceAll(":", "")}`
  const topPadding = 6
  const liquidationY = SPARKLINE_HEIGHT - 12
  const plotBottom = liquidationY - 5
  const min = Math.min(...SPARKLINE_VALUES)
  const max = Math.max(...SPARKLINE_VALUES)
  const range = max - min
  const points = SPARKLINE_VALUES.map((value, index) => ({
    x: (index / (SPARKLINE_VALUES.length - 1)) * SPARKLINE_END_X,
    y: topPadding + ((max - value) / range) * (plotBottom - topPadding),
  }))
  const lastPoint = points.at(-1)
  const linePath = points.reduce((path, point, index) => {
    if (index === 0) {
      return `M ${point.x} ${point.y}`
    }
    const previous = points[index - 1]
    if (!previous) {
      return path
    }
    const midpoint = (previous.x + point.x) / 2
    return `${path} C ${midpoint} ${previous.y}, ${midpoint} ${point.y}, ${point.x} ${point.y}`
  }, "")
  const areaPath = lastPoint
    ? `${linePath} L ${lastPoint.x} ${SPARKLINE_HEIGHT} L 0 ${SPARKLINE_HEIGHT} Z`
    : ""

  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
      preserveAspectRatio="none"
      className="h-13 w-full"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop
            offset="0%"
            stopColor="var(--color-red-500)"
            stopOpacity="0.4"
          />
          <stop
            offset="100%"
            stopColor="var(--color-red-500)"
            stopOpacity="0.08"
          />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path
        d={linePath}
        fill="none"
        stroke="var(--color-red-500)"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1="0"
        x2={SPARKLINE_WIDTH}
        y1={liquidationY}
        y2={liquidationY}
        stroke="white"
        strokeOpacity="0.5"
        strokeWidth="1.25"
        strokeDasharray="3.5 3.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

function CardHeader({ label, icon }: { label: string; icon?: ReactNode }) {
  return (
    <div className="flex items-center gap-2.5">
      {icon ?? <div className="size-5 shrink-0" aria-hidden />}
      <div className="font-medium text-gray-400 text-sm/5 tracking-tight">
        {label}
      </div>
    </div>
  )
}

const AiIllustration = () => {
  return (
    <div className="grid w-full max-w-50 grid-cols-1 gap-3 sm:max-w-[min(85vw,26rem)] sm:grid-cols-2">
      <div className="flex aspect-square flex-col justify-between rounded-3xl bg-gray-800 p-5">
        <CardHeader
          label="News"
          icon={<NewsIcon className="size-5 shrink-0 text-gray-400" />}
        />
        <div className="flex flex-col gap-3">
          <p className="text-balance font-medium text-sm/5 text-white">
            Bitcoin is up on news of additional Saylor purchase.
          </p>
          <div className="flex items-center gap-2">
            <AssetComboIcon
              icon="/static/icons/token/btc.svg"
              sizeClassName="size-8"
            />
            <div className="font-medium text-base/6 text-white tracking-tight">
              BTC
            </div>
            <div className="font-medium text-base/6 text-green-500 tracking-tight">
              +3.59%
            </div>
          </div>
        </div>
      </div>

      <div className="flex aspect-square flex-col justify-between rounded-3xl bg-gray-800 p-5">
        <CardHeader
          label="Earnings"
          icon={<CalendarIcon className="size-5 shrink-0 text-gray-400" />}
        />
        <div className="flex flex-col gap-3">
          <p className="text-balance font-medium text-sm/5 text-white">
            Three companies you hold report earnings this week.
          </p>
          <div className="flex">
            {earningsLogos.map((icon) => (
              <AssetComboIcon
                key={icon}
                icon={icon}
                sizeClassName="size-8"
                className="-ml-2.5 rounded-full outline-[1.5px] outline-gray-800 first:ml-0"
              />
            ))}
          </div>
        </div>
      </div>

      <div className="hidden aspect-square flex-col justify-between rounded-3xl bg-gray-800 p-5 sm:flex">
        <CardHeader
          label="Movers"
          icon={<TrendUpIcon className="size-5 shrink-0 text-gray-400" />}
        />
        <div className="flex flex-col gap-3">
          <p className="text-balance font-medium text-sm/5 text-white">
            ETH is up 8.2% today, making it your largest portfolio mover.
          </p>
          <div className="flex items-center gap-2">
            <AssetComboIcon
              icon="/static/icons/network/ethereum.svg"
              sizeClassName="size-8"
            />
            <div className="font-medium text-base/6 tracking-tight">
              <span className="text-green-500">+$4.5K</span>{" "}
              <span className="text-white">today</span>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden aspect-square flex-col justify-between overflow-hidden rounded-3xl bg-gray-800 sm:flex">
        <div className="px-5 pt-5">
          <CardHeader
            label="Perps"
            icon={<CandlesIcon className="size-5 shrink-0 text-gray-400" />}
          />
        </div>
        <div className="flex flex-col gap-3">
          <p className="text-balance px-5 font-medium text-sm/5 text-white">
            Your SOL position is approaching its liquidation threshold.
          </p>
          <LiquidationSparkline />
        </div>
      </div>
    </div>
  )
}

export default AiIllustration
